import React, { useState } from 'react';
import {
  Card,
  StatTile,
  ScorePill,
  Delta,
  TH,
  TD,
  TD_BASE,
  TD_WRAP,
  Empty,
  Chevron,
  HowCalculated,
  DefinitionTable,
  fmtMins,
} from '../MetricsUI';
import { CountTile } from '../cohort/charts';
import Section from '../Section';
import { OrgLink, HealthPill, HealthHelp, MiniBar, ageLabel, fmtDay, regionText } from './adminShared';

/*
 * Section 2 — accounts, worst health first.
 *
 * This is the "who needs a human this week" view. The prototype framed it around
 * renewal dates and revenue at risk; both are gone. What is left is the part that
 * was actually diagnostic: the activation funnel, the health components, and the
 * cohort shape.
 */

export default function Accounts({ data, onOpenOrg }) {
  const { organizations: orgs, healthWeights, dataGaps } = data;
  const [openId, setOpenId] = useState(null);

  const needsAttention = orgs.filter((o) => o.health.score != null && o.health.score < 55);
  const noneStarted = orgs.filter((o) => o.everStarted === 0);
  const unmeasurable = orgs.filter((o) => o.health.score == null);
  const dormantHeavy = orgs.filter((o) => o.members > 0 && o.dormant / o.members > 0.5);

  const openOrgById = (id) => onOpenOrg(orgs.find((o) => o.organizationId === id));

  return (
    <Section
      title="Accounts"
      subtitle="Sorted worst health first — the accounts that need attention"
      summary={
        <div className="flex flex-wrap gap-4">
          <CountTile
            label="Health under 55"
            value={needsAttention.length}
            tone={needsAttention.length ? 'bad' : 'good'}
            hint={`of ${orgs.length} organisations`}
            items={needsAttention}
            onOpen={openOrgById}
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>Health is a 0&ndash;100 composite of adoption, recency, improvement and seat fill.</li>
                <li>Under 55 is the threshold where an account is worth a conversation.</li>
                <li>Components the platform never captured are dropped, not scored zero.</li>
              </ul>
            }
          />
          <CountTile
            label="Nobody started"
            value={noneStarted.length}
            tone={noneStarted.length ? 'warn' : 'default'}
            items={noneStarted}
            onOpen={openOrgById}
          />
          <CountTile
            label="Mostly dormant"
            value={dormantHeavy.length}
            tone={dormantHeavy.length ? 'warn' : 'default'}
            hint="over half the roster quiet 30 days"
            items={dormantHeavy}
            onOpen={openOrgById}
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>Dormant means someone who has started, then recorded nothing for 30 days.</li>
                <li>People who never started are counted separately, not as dormant.</li>
                <li>Measured from today, not from the end of the selected range.</li>
              </ul>
            }
          />
          <CountTile
            label="Nothing measurable"
            value={unmeasurable.length}
            hint="no health score can be formed"
            items={unmeasurable}
            onOpen={openOrgById}
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>Not a bad score &mdash; no score at all.</li>
                <li>An organisation where nobody has recorded anything has nothing to measure.</li>
                <li>Shown as &mdash; rather than 0, which would read as failing.</li>
              </ul>
            }
          />
          <StatTile
            label="Seat counts on record"
            value={dataGaps.seats.of - dataGaps.seats.missing}
            unit={`/ ${dataGaps.seats.of}`}
            tone={dataGaps.seats.missing ? 'warn' : 'good'}
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>Seats come from the registry subscription&rsquo;s `features.maxUsers`.</li>
                <li>Neither account-creation path writes it, so it is usually absent.</li>
                <li>Where it is missing, seat fill is dropped from health rather than scored zero.</li>
              </ul>
            }
          />
        </div>
      }
    >
      <Card className="overflow-hidden p-0">
        <div className="border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-semibold text-navy">
            {orgs.length} accounts
            <HealthHelp health={orgs[0] && orgs[0].health} weights={healthWeights} />
            <span className="ml-2 text-xs font-normal text-gray-400">
              click a row for the funnel, health components and cohort shape
            </span>
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8" />
                <th className={TH}>Organisation</th>
                <th className={`w-40 ${TH}`}>Account created</th>
                <th className={`w-24 ${TH}`}>Members</th>
                <th className={`w-28 ${TH}`}>Adoption</th>
                <th className={`w-28 ${TH}`}>Median score</th>
                <th className={`w-32 ${TH}`}>Improvement</th>
                <th className={`w-32 ${TH}`}>Usage</th>
                <th className={`w-24 ${TH}`}>Health</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orgs.map((o) => (
                <React.Fragment key={o.organizationId}>
                  <tr
                    onClick={() => setOpenId(openId === o.organizationId ? null : o.organizationId)}
                    className={`cursor-pointer ${
                      openId === o.organizationId ? 'bg-indigo-50/50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="pl-3">
                      <Chevron open={openId === o.organizationId} />
                    </td>
                    <td className={TD_WRAP}>
                      <OrgLink org={o} onOpenOrg={onOpenOrg} />
                      <p className="text-xs text-gray-400">
                        {regionText(o.regions, 'no region on record')}
                      </p>
                    </td>
                    <td className={`${TD_BASE} text-gray-600`}>
                      {fmtDay(o.account.createdAt)}
                      <span className="ml-1 text-xs text-gray-400">{ageLabel(o.account.months)}</span>
                    </td>
                    <td className={`${TD_BASE} tabular-nums text-gray-700`}>{o.members}</td>
                    <td className={`${TD_BASE} tabular-nums text-gray-600`}>
                      {o.adoptionPct == null ? '—' : `${o.adoptionPct}%`}
                    </td>
                    <td className={TD}>
                      <ScorePill pct={o.medianScore} />
                    </td>
                    <td className={TD}>
                      <Delta value={o.avgImprovement} />
                    </td>
                    <td className={`${TD_BASE} text-gray-600`}>
                      {o.usage.attempts === 0 ? (
                        <span className="text-gray-300">nothing yet</span>
                      ) : (
                        <span className="text-xs">
                          {o.usage.negotiations} cases · {o.usage.quizzes} quizzes
                        </span>
                      )}
                    </td>
                    <td className={TD}>
                      <HealthPill health={o.health} />
                    </td>
                  </tr>

                  {openId === o.organizationId && (
                    <tr>
                      <td colSpan={9} className="border-t border-gray-100 bg-gray-50 px-6 py-5">
                        <AccountDetail org={o} weights={healthWeights} onOpenOrg={onOpenOrg} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {orgs.length === 0 && (
                <tr>
                  <td colSpan={9}>
                    <Empty>No organisations with members.</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Section>
  );
}

/** Facts panel cell. */
function Fact({ label, value, tone }) {
  return (
    <div className="rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-gray-200">
      <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`text-sm font-bold ${tone === 'warn' ? 'text-amber-600' : 'text-navy'}`}>{value}</p>
    </div>
  );
}

function AccountDetail({ org: o, weights, onOpenOrg }) {
  const maxN = o.funnel[0].n || 1;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div>
        <h4 className="mb-3 text-sm font-semibold text-navy">
          Activation funnel
          <HowCalculated wide label="What each step means">
            <DefinitionTable
              rows={[
                ['Account exists', 'A user record in this organisation'],
                ['Recorded anything', 'At least one module of any kind'],
                ['Watched a lesson', 'Reached the end of at least one lesson'],
                ['Ran a case study', 'At least one scored negotiation'],
                ['Retried one', 'Went back to the same case study — the strongest signal'],
              ]}
            />
            <span className="block text-gray-500">
              Each step is a subset of the one above, which is what makes the drop-off percentages
              between them meaningful.
            </span>
          </HowCalculated>
        </h4>
        <div className="space-y-1.5">
          {o.funnel.map((f, i) => {
            const prev = i ? o.funnel[i - 1].n : f.n;
            const drop = prev ? Math.round((100 * (prev - f.n)) / prev) : 0;
            return (
              <div key={f.key} className="flex items-center gap-2">
                <span className="w-32 shrink-0 text-xs text-gray-600">{f.label}</span>
                <div className="h-4 flex-1 overflow-hidden rounded bg-gray-100">
                  <div className="h-full rounded bg-lavender" style={{ width: `${(f.n / maxN) * 100}%` }} />
                </div>
                <span className="w-8 shrink-0 text-right text-xs font-semibold tabular-nums text-navy">
                  {f.n}
                </span>
                <span className="w-10 shrink-0 text-right text-[11px] text-rose-500">
                  {i && drop ? `−${drop}%` : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="mb-3 text-sm font-semibold text-navy">
          Health components
          <HealthHelp health={o.health} weights={weights} />
        </h4>
        <div className="space-y-2">
          {o.health.parts.map((part) => {
            const used = o.health.effectiveWeights.find((w) => w.key === part.key);
            return (
              <MiniBar
                key={part.key}
                label={
                  <>
                    {part.label}{' '}
                    <span className="text-gray-400">{used ? `${used.weight}%` : 'not counted'}</span>
                  </>
                }
                value={part.value}
              />
            );
          })}
        </div>
        {o.health.missing.length > 0 && (
          <p className="mt-2 text-[11px] text-gray-400">
            {o.health.missing.join(' and ')} not available for this organisation — the remaining
            weights were rescaled to 100%.
          </p>
        )}
      </div>

      <div>
        <h4 className="mb-3 text-sm font-semibold text-navy">Cohort shape</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <Fact label="Members" value={o.members} />
          <Fact label="Active 30d" value={o.activeLast30} />
          <Fact label="Never started" value={o.neverStarted} tone={o.neverStarted ? 'warn' : undefined} />
          <Fact label="Dormant" value={o.dormant} tone={o.dormant ? 'warn' : undefined} />
          <Fact label="Power users" value={o.power} />
          <Fact
            label="Median score"
            value={o.medianScore == null ? '—' : `${Math.round(o.medianScore)}%`}
          />
          <Fact
            label="Score spread"
            value={
              o.scoreStat.n
                ? `${Math.round(o.scoreStat.p25)}–${Math.round(o.scoreStat.p75)}`
                : '—'
            }
          />
          <Fact label="Time in range" value={o.totalMs ? fmtMins(o.totalMs) : '—'} />
        </div>

        {/*
          Usage as counts. There is no credit tracking in the platform — the
          credits field on an organisation is inert — so a percentage would need
          an invented denominator.
        */}
        <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
          <Fact label="Case studies" value={o.usage.negotiations} />
          <Fact label="Quizzes" value={o.usage.quizzes} />
          <Fact label="Lessons" value={o.usage.lessons} />
        </div>
        <p className="mt-1.5 text-[11px] text-gray-400">
          Modules played in the selected range.
          {o.usage.creditsConfigured
            ? ` Credit allowance on record: ${o.usage.creditsConfigured}.`
            : ' No credit consumption is tracked anywhere yet.'}
        </p>

        <button
          onClick={() => onOpenOrg(o)}
          className="mt-4 w-full rounded-md bg-navy px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
        >
          Open {o.name} org view →
        </button>
      </div>
    </div>
  );
}
